<script setup lang="ts">
const { organization } = defineProps<{
  organization: Organization
}>()

const { t, locale } = useI18n({
  useScope: 'local',
})

// If there's a logo, show it full width
const fullWidthLogo = !!organization.logoUrl

// TODO: only for admins or not?
const { isAuthenticated } = useAuth()
const showLink = isAuthenticated
</script>

<template>
  <BaseCard v-if="organization" class="p-8">
    <VacancyDetailsSidebarCardTitle>
      {{ t('aboutThisOrganization') }}
    </VacancyDetailsSidebarCardTitle>

    <div
      :class="{ 'flex-col': fullWidthLogo, 'items-center': !fullWidthLogo }"
      class="mb-4 flex gap-2"
    >
      <div :class="{ 'flex w-full items-center justify-center gap-2': fullWidthLogo }">
        <OrganizationLogo
          v-if="fullWidthLogo && organization.logoUrl"
          :logo-url="organization.logoUrl"
          :name="organization.name"
        />
        <OrganizationAvatar
          v-else
          :organization="organization"
          size="sm"
        />
      </div>
      <div v-if="!organization.logoUrl">
        <BaseHeading
          tag="h5"
          size="lg"
          weight="medium"
          lead="normal"
          class="line-clamp-1"
        >
          {{ organization.name }}
        </BaseHeading>
        <BaseParagraph size="sm" class="text-muted-400">
          {{ organization.city }}
        </BaseParagraph>
      </div>
    </div>

    <div v-if="organization.website" class="mt-4 flex items-center gap-2">
      <BaseButton
        v-if="organization.website?.startsWith('https://')"
        shape="rounded"
        class="w-full"
        :to="`${organization.website}`"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon :name="$t('organization.icons.website')" class="size-4" />
        <span class="overflow-hidden truncate">{{ organization.website.replace('https://', '').replace('http://', '') }}</span>
      </BaseButton>
    </div>

    <div v-if="showLink" class="mt-4 flex items-center gap-2">
      <BaseButton
        shape="rounded"
        class="w-full"
        :to="`/organizations/${organization.id}`"
      >
        <Icon name="ph:buildings" class="size-4" />
        <span>{{ t('viewDetails') }}</span>
      </BaseButton>
    </div>
  </BaseCard>
</template>

<i18n lang="json">
{
  "en": {
    "aboutThisOrganization": "About this organization",
    "viewDetails": "View profile"
  },
  "nl": {
    "aboutThisOrganization": "Over deze organisatie",
    "viewDetails": "Bekijk profiel"
  }
}
</i18n>
